import type { ComponentProps } from 'react';
import PalmettoSideMenu from '@zawarski/palmetto-react-side-menu';
import {
  BookOpen,
  Building2,
  ClipboardList,
  House,
  LogOut,
  MapIcon,
  MessageCircleQuestion,
  PanelBottom,
  Rows3,
  SquareUser,
  User,
  Users,
} from 'lucide-react';

/** The menu shape the package draws (it exports the component, not its types). */
export type SideMenuItems = ComponentProps<typeof PalmettoSideMenu>['menuItems'];

/** The damage assessment user guide, from the app's base so nested routes do not change it. */
export const USER_GUIDE_URL = `${import.meta.env.BASE_URL}pdf/DamageAssessment.pdf`;

/** The PA entry's path. Choosing it opens or closes its sub-menu instead of a page. */
export const PA_PATH = 'pa';

/** The PA entry's key, which the package matches against `activePath`. */
export const PA_KEY = 'pa';

/**
 * Paths of the rows under the menu that run an action instead of opening a page. The package
 * hands the chosen row's `path` to `onMenuClick`, so these rows are told apart by theirs; the `#`
 * keeps them from ever matching a route.
 */
export const SECTION_PATHS = {
  user: '#user',
  userGuide: '#user-guide',
  contactSupport: '#contact-support',
  logout: '#logout',
} as const;

/** The PA sub-menu, one row per PA page. */
const PA_SUB_MENU = [
  { key: 'general-info', name: 'General Information', path: '/pa/general-info' },
  { key: 'short-form', name: 'Short Form Summary', path: '/pa/short-form' },
  { key: 'debris-removal', name: 'Debris Removal', path: '/pa/a' },
  { key: 'emergency', name: 'Emergency Protective Measure', path: '/pa/b' },
  { key: 'roads', name: 'Roads and Bridges', path: '/pa/c' },
  { key: 'water', name: 'Water Control Facilities', path: '/pa/d' },
  { key: 'buildings', name: 'Buildings and Equipment', path: '/pa/e' },
  { key: 'utilities', name: 'Utilities', path: '/pa/f' },
  { key: 'parks', name: 'Parks and Recreation', path: '/pa/g' },
];

type MenuEntry = SideMenuItems['menu'][number] & {
  /** The entry owns every route at or under this prefix, for active marking. */
  routePrefix: string;
  /** Only admin and finance users see the entry. */
  restricted?: boolean;
};

/** The top-level entries: what the package draws, which routes each owns, and who sees it. */
const MENU_ENTRIES: MenuEntry[] = [
  {
    key: 'dashboard',
    name: 'Home',
    path: '/dashboard',
    routePrefix: '/dashboard',
    startIcon: <House />,
  },
  { key: 'map', name: 'Map', path: '/map', routePrefix: '/map', startIcon: <MapIcon /> },
  {
    key: 'equipment',
    name: 'Equipment',
    path: '/equipment',
    routePrefix: '/equipment',
    restricted: true,
    startIcon: <PanelBottom />,
  },
  {
    key: 'personnel',
    name: 'Personnel',
    path: '/personnel',
    routePrefix: '/personnel',
    restricted: true,
    startIcon: <Building2 />,
  },
  {
    key: 'teams',
    name: 'Teams',
    path: '/teams',
    routePrefix: '/teams',
    startIcon: <Users />,
  },
  {
    key: 'ia',
    name: 'Individual assistance (IA)',
    path: '/ia',
    routePrefix: '/ia',
    startIcon: <SquareUser />,
  },
  {
    key: PA_KEY,
    name: 'Public assistance (PA)',
    path: PA_PATH,
    routePrefix: '/pa',
    startIcon: <ClipboardList />,
    subMenu: PA_SUB_MENU,
  },
  {
    key: 'category',
    name: 'Category B: Extended',
    path: '/category/cost',
    routePrefix: '/category',
    restricted: true,
    startIcon: <Rows3 />,
  },
];

const isUnder = (pathname: string, prefix: string) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export type SideMenuAction =
  | 'navigate'
  | 'close'
  | 'toggleSubMenu'
  | 'userGuide'
  | 'contactSupport'
  | 'logout';

/** What choosing a row does: the rows under the menu run their action, every entry opens its route. */
export const sideMenuAction = (path: string): SideMenuAction => {
  switch (path) {
    case SECTION_PATHS.userGuide:
      return 'userGuide';
    case SECTION_PATHS.contactSupport:
      return 'contactSupport';
    case SECTION_PATHS.logout:
      return 'logout';
    case PA_PATH:
      return 'toggleSubMenu';
    default:
      // The user-name row, and anything else that is not a route, only closes the menu.
      return path.startsWith('/') ? 'navigate' : 'close';
  }
};

/**
 * The `activePath` to hand the package. The package marks an entry active when
 * `activePath.includes(entry.key)`, which the raw pathname does not fit ('/pa/a' holds no
 * 'debris-removal'). So the route resolves to its entry's key, a PA page to `pa:<sub-key>` so the
 * PA entry and its row are both active, and '' on a route no entry owns. While the PA sub-menu is
 * open on another page the PA entry is the active one, as the package shows only the active parent
 * in sub-menu mode.
 */
export const sideMenuActivePath = (pathname: string, subMenuOpen: boolean): string => {
  const paPage = PA_SUB_MENU.find((subItem) => isUnder(pathname, subItem.path));
  if (paPage) return `${PA_KEY}:${paPage.key}`;
  if (subMenuOpen) return PA_KEY;
  const entry = MENU_ENTRIES.find((item) => isUnder(pathname, item.routePrefix));
  return entry ? String(entry.key) : '';
};

/**
 * The entries this user may open, then the user / applicants / guide / contact-support / log-out
 * rows and the version line.
 */
export const buildSideMenuItems = ({
  showRestricted,
  userFullName,
  iconColor,
  logoutColor,
  mutedColor,
}: {
  showRestricted: boolean;
  userFullName: string;
  iconColor: string;
  logoutColor: string;
  mutedColor: string;
}): SideMenuItems => {
  return {
    menu: MENU_ENTRIES.filter((item) => showRestricted || !item.restricted).map(
      ({ routePrefix: _routePrefix, restricted: _restricted, ...item }) => item
    ),
    section: [
      {
        key: 'user',
        name: userFullName,
        path: SECTION_PATHS.user,
        startIcon: <User color={iconColor} />,
      },
      {
        key: 'applicants',
        name: 'Manage applicants',
        path: '/applicants',
        endIcon: <Users color={mutedColor} />,
      },
      {
        key: 'guide',
        name: 'User guide',
        path: SECTION_PATHS.userGuide,
        endIcon: <BookOpen color={iconColor} />,
      },
      {
        key: 'contactSupport',
        name: 'Contact Support',
        path: SECTION_PATHS.contactSupport,
        endIcon: <MessageCircleQuestion color={iconColor} />,
      },
      {
        key: 'logout',
        name: 'Log out',
        path: SECTION_PATHS.logout,
        endIcon: <LogOut color={logoutColor} />,
      },
    ],
    version: { name: 'Version 3.0.0', color: '#999' },
  };
};
